import { lazy, Suspense, useEffect, useMemo, useState, type FormEvent } from 'react'
import { AlertCircle, CalendarDays, ChartNoAxesCombined, RefreshCw } from 'lucide-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { getDailyStats, type MerchantDailyStats, type DashboardWindow, type DailyStatsQuery } from '../../services/dashboard'
import { count, money } from './format'
import { paymentMethodLabels } from '../../lib/payment-method-labels'
import type { PaymentMethod } from '../../types'
import './DashboardTrends.css'

dayjs.extend(utc)
dayjs.extend(timezone)
const DailyTrendChart = lazy(() => import('./DailyTrendChart'))
const ZONE = 'Asia/Shanghai'
const dateLabel = (timestamp: number) => dayjs(timestamp).tz(ZONE).format('YYYY-MM-DD')

export default function DashboardTrends() {
  const [period, setPeriod] = useState<DashboardWindow>('all')
  const [query, setQuery] = useState<DailyStatsQuery>({ window: 'all' })
  const [draftStart, setDraftStart] = useState(() => dayjs().tz(ZONE).format('YYYY-MM-DD'))
  const [draftEnd, setDraftEnd] = useState(() => dayjs().tz(ZONE).format('YYYY-MM-DD'))
  const [rangeError, setRangeError] = useState<string | null>(null)
  const [data, setData] = useState<MerchantDailyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setData(null)
    setError(null)
    getDailyStats(query)
      .then((response) => { if (!cancelled) setData(response.data) })
      .catch((failure: unknown) => {
        if (!cancelled) setError(failure instanceof Error ? failure.message : '載入交易統計失敗')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [query, retry])

  const days = useMemo(() => {
    if (!data) return []
    // The API returns the complete selected range, including zero-activity days.
    return [...data.days].sort((a, b) => a.dayStart - b.dayStart)
  }, [data])
  const chartData = useMemo(() => days.map((day) => ({ ...day, date: dateLabel(day.dayStart), flow: Number(day.flowAmountUsd) })), [days])
  const summary = data?.summary
  const unconverted = summary?.unconvertedOrderCount ?? 0
  const metrics = [
    { label: '完成流水', value: summary ? money(summary.flowAmountUsd) : '—', unit: 'USD' },
    { label: '创建订单', value: summary ? count(summary.createdOrderCount) : '—', unit: '筆' },
    { label: '完成订单', value: summary ? count(summary.paidOrderCount) : '—', unit: '筆' },
    { label: '付款完成率', value: summary ? `${(Number(summary.completionRate) * 100).toFixed(2)}%` : '—' },
    { label: '平均客单价', value: summary?.averageTicketUsd != null ? money(summary.averageTicketUsd) : '—', unit: 'USD' },
  ]

  function applyCustomRange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const valid = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid() && dayjs(value).format('YYYY-MM-DD') === value
    if (!valid(draftStart) || !valid(draftEnd)) { setRangeError('請輸入有效的開始日期與結束日期。'); return }
    const from = dayjs.tz(draftStart, ZONE).startOf('day')
    const to = dayjs.tz(draftEnd, ZONE).startOf('day')
    if (to.isBefore(from)) { setRangeError('結束日期不得早於開始日期。'); return }
    if (to.diff(from, 'day') >= 90) { setRangeError('日期範圍最多為 90 天，請縮短日期範圍。'); return }
    if (draftEnd > dayjs().tz(ZONE).format('YYYY-MM-DD')) { setRangeError('日期不得晚於今天。'); return }
    setRangeError(null)
    setQuery({ window: 'custom', startDate: from.valueOf(), endDate: to.endOf('day').valueOf() })
  }

  return <section aria-label="交易統計" className="dashboard-trends space-y-4">
    <div className="dashboard-trends-toolbar">
      <div><h2 className="text-lg font-semibold">交易統計</h2><p className="mt-1 text-xs text-muted-foreground">北京時間（UTC+8） · {data ? `${dateLabel(data.startDate)} 至 ${dateLabel(data.endDate)}` : '正在載入日期範圍'} · 今日為即時累計</p></div>
      <div role="group" aria-label="統計期間" className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1">
        {([['custom', '自定义'], ['1d', '1天'], ['7d', '7天'], ['14d', '14天'], ['30d', '30天'], ['month', '本月至今'], ['all', '全时']] as const).map(([value, label]) => <Button key={value} size="sm" variant={period === value ? 'default' : 'ghost'} aria-pressed={period === value} onClick={() => {
          setPeriod(value)
          setRangeError(null)
          if (value !== 'custom') setQuery({ window: value })
        }}>{label}</Button>)}
      </div>
    </div>
    {period === 'custom' && <form noValidate onSubmit={applyCustomRange} aria-label="自訂統計日期" className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
      <label className="grid min-w-0 flex-1 gap-1.5 text-xs font-medium" htmlFor="trend-start">開始日期<Input id="trend-start" type="date" placeholder="開始日期" value={draftStart} max={dayjs().tz(ZONE).format('YYYY-MM-DD')} onChange={(event) => setDraftStart(event.target.value)} aria-invalid={!!rangeError} aria-describedby={rangeError ? 'trend-range-error' : undefined} /></label>
      <label className="grid min-w-0 flex-1 gap-1.5 text-xs font-medium" htmlFor="trend-end">結束日期<Input id="trend-end" type="date" placeholder="結束日期" value={draftEnd} max={dayjs().tz(ZONE).format('YYYY-MM-DD')} onChange={(event) => setDraftEnd(event.target.value)} aria-invalid={!!rangeError} aria-describedby={rangeError ? 'trend-range-error' : undefined} /></label>
      <Button type="submit"><CalendarDays size={15} />套用日期</Button>
    </form>}
    <p className="text-xs leading-relaxed text-muted-foreground">按所選期間創建的訂單統計，完成訂單與流水歸入創建日期，跨日付款亦包含；完成指已付款，含後續退款、爭議及拒付的歷史成功收款，流水不扣除手續費或退款。</p>
    {rangeError && <div id="trend-range-error" role="alert" className="dashboard-notice dashboard-notice-warning"><AlertCircle size={18} />{rangeError}</div>}
    {error ? <div role="alert" className="dashboard-notice dashboard-notice-error"><AlertCircle size={18} /><span className="flex-1">{error}</span><Button variant="outline" size="sm" onClick={() => setRetry((value) => value + 1)}><RefreshCw size={14} />重試</Button></div> : <>
      <div className="dashboard-trends-metrics" aria-label="所選期間匯總" aria-busy={loading}>
        {metrics.map(({ label, value, unit }) => <Card key={label} aria-label={label}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}{unit === 'USD' ? '（USD）' : ''}</p><strong className="mt-2 block text-2xl font-semibold tabular-nums">{value}</strong></CardContent></Card>)}
      </div>
      {unconverted > 0 && <div role="alert" className="dashboard-notice dashboard-notice-warning"><AlertCircle size={18} />{count(unconverted)} 筆已支付訂單缺少歷史匯率，暫未計入流水。</div>}
      {!loading && summary?.createdOrderCount === 0 && summary.paidOrderCount === 0 && <div role="status" className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-5 text-sm text-muted-foreground"><ChartNoAxesCombined size={18} />所選期間暫無訂單</div>}
      <div className="space-y-4" aria-busy={loading}>
        <Card><CardHeader><h3 className="font-semibold">交易趋势</h3><p className="text-xs text-muted-foreground">每日流水、創建訂單與支付成功訂單 · 左軸 USD，右軸筆數</p></CardHeader><CardContent>
          <div className="dashboard-trends-legend" aria-label="圖例"><span><i className="bg-[#0d6efd]" />流水（USD）</span><span><i className="bg-[#533afd]" />創建訂單</span><span><i className="bg-[#16a34a]" />支付成功訂單</span></div>
          <div role="img" aria-label="每日流水與訂單趨勢圖" className="dashboard-trends-chart">{loading ? <ChartLoading /> : <Suspense fallback={<ChartLoading />}><DailyTrendChart data={chartData} /></Suspense>}</div>
        </CardContent></Card>
      </div>
      <Card aria-busy={loading}><CardHeader><h3 className="font-semibold">支付方式表现</h3><p className="text-xs text-muted-foreground">按所選期間創建的訂單統計 · 按完成流水降序排列</p></CardHeader><CardContent>
        <Table aria-label="支付方式表现">
          <caption className="sr-only">所選期間各支付方式表現，金額單位 USD；缺少歷史匯率的完成訂單暫未計入流水，平均客單價顯示 —。</caption>
          <TableHeader><TableRow>
            <TableHead scope="col">支付方式</TableHead>
            <TableHead scope="col" className="text-right">创建订单</TableHead>
            <TableHead scope="col" className="text-right">完成订单</TableHead>
            <TableHead scope="col" className="text-right">完成流水（USD）</TableHead>
            <TableHead scope="col" className="text-right">付款完成率</TableHead>
            <TableHead scope="col" className="text-right">平均客单价（USD）</TableHead>
          </TableRow></TableHeader>
          <TableBody>{loading ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">正在載入支付方式表現…</TableCell></TableRow> : (data?.methods ?? []).length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">所選期間暫無支付方式數據</TableCell></TableRow> : data?.methods.map((method) => <TableRow key={method.paymentMethod}>
            <TableCell>{paymentMethodLabels[method.paymentMethod as PaymentMethod] ?? '其他'}{method.unconvertedOrderCount > 0 && <span className="mt-1 block text-xs text-amber-700">{count(method.unconvertedOrderCount)} 筆未計入流水</span>}</TableCell>
            <TableCell className="text-right tabular-nums">{count(method.createdOrderCount)}</TableCell>
            <TableCell className="text-right tabular-nums">{count(method.paidOrderCount)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(method.flowAmountUsd)}</TableCell>
            <TableCell className="text-right tabular-nums">{(Number(method.completionRate) * 100).toFixed(2)}%</TableCell>
            <TableCell className="text-right tabular-nums">{method.unconvertedOrderCount > 0 || method.averageTicketUsd == null ? '—' : money(method.averageTicketUsd)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </CardContent></Card>
    </>}
  </section>
}
function ChartLoading() {
  return <div role="status" aria-label="正在載入交易統計" className="h-full animate-pulse rounded-lg bg-muted" />
}
